"use client"
import { useDebounceValue } from "@opensea/ui-kit/hooks"
import { useSubscriptionStore } from "./subscription-store"

type DegradedReason = "offline" | "unknown"

type SubscriptionStatusResult =
  | { status: "live" }
  | { status: "paused" }
  | { status: "degraded"; reason: DegradedReason }

export const useSubscriptionStatus = ():
  | SubscriptionStatusResult
  | undefined => {
  const subscriptions = useSubscriptionStore(state => state.subscriptions)

  function getStatus(): SubscriptionStatusResult | undefined {
    const subscriptionsList = Array.from(subscriptions.values())

    if (subscriptionsList.length === 0) {
      return undefined
    }

    if (subscriptionsList.every(subscription => subscription.paused)) {
      return { status: "paused" }
    }

    if (subscriptionsList.some(subscription => subscription.hasError)) {
      return { status: "degraded", reason: "unknown" }
    }

    return { status: "live" }
  }

  const status = getStatus()

  const [debouncedStatus] = useDebounceValue(status, 1000)

  // We return the debounced status so we don't have periods of 0 subscriptions and thus
  // no live indicator in between page transitions
  // However, if the status is already there, show it immediately.
  return status ?? debouncedStatus
}
